import * as React from 'react';
import { RadioButton as RadioButtonIcon, RadioButtonSelected as RadioButtonSelectedIcon, } from '@volkswagen-onehub/icons-core';
import { Text, TextTag, TokenTextAppearance, TokenTextColor } from '../text';
import { css, styled } from '../theme-provider';
import { getCommonProps } from '../helpers/common-props';
import { createA11yOutlineStyles } from '../helpers/a11y';
import { useThrottleCallback } from '@react-hook/throttle';
const noop = () => undefined;
const StyledLabel = styled.label.withConfig({ displayName: "StyledLabel", componentId: "sc-12cr2ii" }) `position:relative;color:${({ theme }) => theme.colors.content.primary};cursor:pointer;`;
const StyledRadioWrapper = styled.span.withConfig({ displayName: "StyledRadioWrapper", componentId: "sc-1n1yaaw" }) `display:flex;align-items:center;color:${({ theme }) => theme.colors.content.primary};> svg{overflow:visible;}> svg:last-child{position:absolute;display:block;opacity:0;}`;
const transition = css `transition:fill ${({ theme }) => theme.animation.duration.color} ease,color ${({ theme }) => theme.animation.duration.color} ease;`;
const StyledFocusWrapper = styled.span.withConfig({ displayName: "StyledFocusWrapper", componentId: "sc-boy2eb" }) `position:relative;display:grid;grid-column-gap:${({ theme }) => theme.size.static200};grid-template-columns:max-content auto;${({ multiline }) => (multiline ? `align-items: flex-start;` : `align-items: center;`)}:hover{${({ theme, disabled }) => !disabled &&
    css `
				color: ${theme.colors.controllers.off.hover};
				${StyledRadioWrapper} {
					color: ${theme.colors.controllers.off.hover};
				}
			`}}:not(:hover){${StyledRadioWrapper}{${transition};}}`;
const StyledInput = styled.input.withConfig({ displayName: "StyledInput", componentId: "sc-l2strf" }) `position:absolute;opacity:0;outline:0;user-select:none;pointer-events:none;:focus-visible{+ ${StyledFocusWrapper}{${StyledRadioWrapper}{${({ theme }) => createA11yOutlineStyles(theme)};}}}:disabled{+ ${StyledFocusWrapper}{cursor:not-allowed;color:${({ theme }) => theme.colors.content.disabled};${StyledRadioWrapper}{color:${({ theme }) => theme.colors.controllers.off.disabled};}}}:checked{+ ${StyledFocusWrapper}{${StyledRadioWrapper}{${transition};color:${({ theme, disabled }) => disabled
    ? theme.colors.controllers.on.disabled
    : theme.colors.controllers.on.default};&:hover{${({ theme, disabled }) => !disabled &&
    css `
							color: ${theme.colors.controllers.on.hover};
						`}}> svg:last-child{opacity:1;}}}}`;
export const RadioButton = (props) => {
    const textRef = React.useRef(null);
    const [multiline, setMultiline] = React.useState(false);
    const detectLineHeight = React.useCallback(() => {
        const wrapper = textRef === null || textRef === void 0 ? void 0 : textRef.current;
        if (!wrapper) {
            return;
        }
        const height = wrapper.getBoundingClientRect().height;
        // magic number 36: if the height of the text is greater than 36px there are probably multiple lines of text in there.
        if (height > 36 !== multiline) {
            setMultiline(!multiline);
        }
    }, [multiline]);
    const handleResize = useThrottleCallback(React.useCallback(() => {
        detectLineHeight();
    }, [detectLineHeight]));
    // detect header height and multiline on window resize
    React.useEffect(() => {
        const localHandleResize = handleResize;
        window.addEventListener('resize', localHandleResize);
        return () => window.removeEventListener('resize', localHandleResize);
    }, [handleResize]);
    // initially detect header height and multiline
    React.useEffect(() => {
        handleResize();
    }, [handleResize]);
    const handleChange = React.useCallback((e) => {
        const { onChange = noop } = props;
        onChange(e, props);
    }, [props]);
    const handleBlur = React.useCallback((e) => {
        const { onBlur = noop } = props;
        onBlur(e, props);
    }, [props]);
    const handleFocus = React.useCallback((e) => {
        const { onFocus = noop } = props;
        onFocus(e, props);
    }, [props]);
    const handleClick = React.useCallback((e) => {
        const { onClick = noop } = props;
        onClick(e, props);
    }, [props]);
    const { defaultChecked, checked, disabled, id, name, textAppearance = TokenTextAppearance.copy200, value, label = '', results, } = props;
    const commonProps = getCommonProps(props);
    return (React.createElement(StyledLabel, Object.assign({}, commonProps),
        React.createElement(StyledInput, { defaultChecked: defaultChecked, checked: checked, disabled: disabled, id: id, name: name, onBlur: handleBlur, onChange: handleChange, onClick: handleClick, onFocus: handleFocus, type: "radio", value: value }),
        React.createElement(StyledFocusWrapper, { disabled: disabled, multiline: multiline },
            React.createElement(StyledRadioWrapper, null,
                React.createElement(RadioButtonIcon, { variant: "default", "aria-hidden": true }),
                React.createElement(RadioButtonSelectedIcon, { variant: "default", "aria-hidden": true })),
            React.createElement("span", { ref: textRef }, label &&
                (typeof label === 'string' ? (React.createElement(Text, { tag: TextTag.span, color: TokenTextColor.inherit, appearance: textAppearance },
                    label,
                    " ",
                    results && `(${results})`)) : (label))))));
};
